// A port (DESIGN.md "Bring your numbers", mockups f to h3): the request form while it is a draft
// (account, move, bill, where the numbers land), Review and sign, then the status page with its
// timeline, the carrier's fix request and the numbers. Admins write; managers read.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { InfoButton, SegmentedControl, Surface, Table, TBody, Td, THead, Th, Tr } from "@vd/ui";
import { AlertTriangle, Check, FileText, Phone, Upload } from "lucide-react";
import { type ChangeEvent, type ReactNode, useMemo, useRef, useState } from "react";
import { brandsQuery, usersQuery } from "../../../../api/admin";
import { ApiError, errorMessage } from "../../../../api/client";
import { addressesQuery, purchaseSettingsQuery } from "../../../../api/numbers";
import { type Port, portKeys, portQuery, ports, uploadBill } from "../../../../api/ports";
import { requireManager, useCanEdit } from "../../../../auth/useMe";
import { Crumbs } from "../../../../components/Crumbs";
import { AddressDialog } from "../../../../components/numbers/AddressDialog";
import { SignDialog } from "../../../../components/ports/SignDialog";
import { toast } from "../../../../components/toast";
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  Select,
  Spinner,
  Textarea,
} from "../../../../components/ui";
import { level } from "../../../../lib/crumbs";
import { fmtPhone } from "../../../../lib/format";
import { addressLine } from "../../../../lib/numbers";
import {
  canCancel,
  editOf,
  focText,
  formOf,
  numberState,
  numbersCount,
  type PortForm,
  portName,
  STATE_BADGE,
  type StepMark,
  shortDate,
  showsForm,
  signBlockers,
  stepTime,
  timeline,
} from "../../../../lib/ports";
import { US_STATES } from "../../../../lib/usStates";

const MAX_BILL = 10 * 1024 * 1024;
const ADD = "__add_address__";

function usePortName(id: string): string | null {
  const p = useQuery(portQuery(id)).data;
  return p ? portName(p) : null;
}

export const Route = createFileRoute("/admin/numbers/ports/$portId")({
  staticData: {
    crumb: {
      label: "Port",
      useLabel: ({ params }) => usePortName(params.portId ?? ""),
      parents: [level("Numbers", "/admin/numbers"), level("Ports", "/admin/numbers?tab=ports")],
    },
  },
  beforeLoad: requireManager,
  loader: ({ context, params }) => context.queryClient.prefetchQuery(portQuery(params.portId)),
  component: PortPage,
});

function PortPage() {
  const { portId } = Route.useParams();
  const q = useQuery(portQuery(portId));
  if (q.isPending) return <Loading />;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.code === "porting_off")
      return <EmptyState title={q.error.message} />;
    return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  }
  return <PortScreen key={`${q.data.id}:${q.data.version}`} port={q.data} />;
}

function Header({ port, actions, meta }: { port: Port; actions?: ReactNode; meta?: ReactNode }) {
  const badge = STATE_BADGE[port.state];
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 pb-5">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="-mt-1 mb-1 empty:hidden">
          <Crumbs />
        </div>
        <div className="flex min-w-0 items-center gap-2.5">
          <h1 className="truncate font-display font-medium text-display">{portName(port)}</h1>
          <Badge tone={badge.tone}>{badge.label}</Badge>
        </div>
        {meta ? <p className="text-caption text-ink-3">{meta}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

function PortScreen({ port }: { port: Port }) {
  const canEdit = useCanEdit();
  const [fixing, setFixing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const form = showsForm(port.state) || (fixing && port.state === "needs_info");
  return (
    <>
      {form ? (
        <FormScreen port={port} readOnly={!canEdit} />
      ) : (
        <StatusScreen
          port={port}
          canEdit={canEdit}
          onFix={() => setFixing(true)}
          onCancel={() => setCancelling(true)}
        />
      )}
      {cancelling ? <CancelDialog port={port} onClose={() => setCancelling(false)} /> : null}
    </>
  );
}

// The request form ------------------------------------------------------------------------------

function Panel({ title, help, children }: { title: string; help?: string; children: ReactNode }) {
  return (
    <Surface asChild className="flex min-w-0 flex-col">
      <section aria-label={title}>
        <header className="flex min-h-12 items-center gap-1.5 px-4 pt-2">
          <h2 className="font-medium text-title">{title}</h2>
          {help ? (
            <InfoButton kind="help" label={`About ${title}`}>
              {help}
            </InfoButton>
          ) : null}
        </header>
        <div className="flex flex-col gap-3 px-4 pb-4">{children}</div>
      </section>
    </Surface>
  );
}

function FormScreen({ port, readOnly }: { port: Port; readOnly: boolean }) {
  const qc = useQueryClient();
  const [f, setF] = useState<PortForm>(() => formOf(port));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<"save" | "sign" | "submit" | null>(null);
  const [signing, setSigning] = useState<Port | null>(null);
  const inFlight = useRef(false);
  const carrier = port.losing_carrier;
  const blockers = signBlockers(f, port);
  const resigning = port.state !== "draft";

  const set = <K extends keyof PortForm>(k: K, v: PortForm[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    setDirty(true);
  };
  const setNumber = (e164: string, patch: Partial<PortForm["numbers"][number]>) => {
    setF((x) => ({ ...x, numbers: x.numbers.map((n) => (n.e164 === e164 ? { ...n, ...patch } : n)) }));
    setDirty(true);
  };

  const store = (p: Port) => {
    qc.setQueryData(portKeys.port(p.id), p);
    void qc.invalidateQueries({ queryKey: portKeys.list });
  };

  /** Saves the form; answers the port as saved, or null when the save failed. */
  const save = async (): Promise<Port | null> => {
    try {
      const p = await ports.edit(port.id, editOf(f));
      setDirty(false);
      return p;
    } catch (e) {
      toast(errorMessage(e), "danger");
      return null;
    }
  };

  const run = async (kind: "save" | "sign" | "submit") => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(kind);
    try {
      if (kind === "submit") {
        store(await ports.submit(port.id));
        toast("Port sent");
        return;
      }
      const p = dirty || kind === "save" ? await save() : port;
      if (!p) return;
      if (kind === "save") {
        toast("Saved");
        store(p);
      } else {
        // The cache takes the saved port when the dialog closes: a new version remounts the form.
        setSigning(p);
      }
    } catch (e) {
      toast(errorMessage(e), "danger");
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  };

  const signedAndClean = port.state === "signed" && !dirty;
  return (
    <>
      <Header
        port={port}
        actions={
          readOnly ? null : (
            <div className="flex flex-col items-end gap-1.5">
              {resigning ? (
                <p className="flex items-center gap-1.5 text-caption text-warning">
                  <AlertTriangle className="size-3.5" aria-hidden />
                  Changes need a new signature.
                </p>
              ) : null}
              <div className="flex items-center gap-2">
                <Button onClick={() => void run("save")} disabled={busy !== null}>
                  Save draft
                </Button>
                {signedAndClean ? (
                  <Button variant="primary" onClick={() => void run("submit")} disabled={busy !== null}>
                    Submit
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={() => void run("sign")}
                    disabled={busy !== null || blockers.length > 0}
                  >
                    Review and sign
                  </Button>
                )}
              </div>
            </div>
          )
        }
      />
      <fieldset disabled={readOnly} className="contents">
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <Panel title="Account" help={`As it appears on your ${carrier} bill.`}>
            <Field label="Account holder">
              <Input value={f.account_holder} onChange={(e) => set("account_holder", e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Authorized person">
                <Input value={f.authorized_name} onChange={(e) => set("authorized_name", e.target.value)} />
              </Field>
              <Field label="Title">
                <Input value={f.authorized_title} onChange={(e) => set("authorized_title", e.target.value)} />
              </Field>
              <Field label="Billing number">
                <Input
                  inputMode="tel"
                  value={f.billing_number ? fmtPhone(f.billing_number) : ""}
                  onChange={(e) => set("billing_number", e.target.value)}
                />
              </Field>
              <Field label="Account number">
                <Input
                  value={f.account_number}
                  placeholder={port.account_number_last4 ? `Ends in ${port.account_number_last4}` : undefined}
                  onChange={(e) => set("account_number", e.target.value)}
                  autoComplete="off"
                />
              </Field>
            </div>
            <Field label="PIN" help={`Only if ${carrier} gave the account a PIN or passcode.`}>
              <Input
                type="password"
                value={f.pin}
                placeholder={port.pin_set ? "••••" : undefined}
                onChange={(e) => set("pin", e.target.value)}
                autoComplete="off"
              />
            </Field>
            <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3">
              <Field label="Service street">
                <Input value={f.street} onChange={(e) => set("street", e.target.value)} />
              </Field>
              <Field label="Unit">
                <Input value={f.unit} onChange={(e) => set("unit", e.target.value)} />
              </Field>
              <Field label="ZIP">
                <Input inputMode="numeric" value={f.zip} onChange={(e) => set("zip", e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City">
                <Input value={f.city} onChange={(e) => set("city", e.target.value)} />
              </Field>
              <Field label="State">
                <Select
                  value={f.state}
                  onValueChange={(v) => set("state", v)}
                  disabled={readOnly}
                  placeholder="State"
                  options={US_STATES.map((s) => ({ value: s.code, label: s.name }))}
                />
              </Field>
            </div>
          </Panel>
          <div className="flex min-w-0 flex-col gap-4">
            <Panel title="Move">
              <Field label="Numbers" help={`Some: the rest stay with ${carrier} on the same account.`}>
                <SegmentedControl
                  aria-label="Numbers"
                  value={f.kind}
                  onValueChange={(v) => set("kind", v)}
                  options={[
                    { value: "full", label: "All on the account" },
                    { value: "partial", label: "Some" },
                  ]}
                />
              </Field>
              {f.kind === "partial" ? (
                <Field label="What stays">
                  <Textarea
                    rows={3}
                    value={f.partial_remaining}
                    onChange={(e) => set("partial_remaining", e.target.value)}
                  />
                </Field>
              ) : null}
              <Field
                label="Date"
                help={`${shortDate(port.earliest_foc_date)} is the earliest. Keep ${carrier} service on until the switch.`}
              >
                <Input
                  type="date"
                  min={port.earliest_foc_date}
                  value={f.requested_foc_date}
                  onChange={(e) => set("requested_foc_date", e.target.value)}
                />
              </Field>
            </Panel>
            <BillPanel port={port} readOnly={readOnly} onUploaded={store} />
          </div>
          <div className="lg:col-span-2">
            <LandingPanel form={f} readOnly={readOnly} onChange={setNumber} />
          </div>
        </div>
      </fieldset>
      {signing ? (
        <SignDialog
          port={signing}
          onClose={() => {
            setSigning(null);
            store(signing);
          }}
          onSent={(p) => {
            setSigning(null);
            store(p);
          }}
        />
      ) : null}
    </>
  );
}

function fileSize(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

function BillPanel({
  port,
  readOnly,
  onUploaded,
}: {
  port: Port;
  readOnly: boolean;
  onUploaded: (p: Port) => void;
}) {
  const [file, setFile] = useState<{ name: string; size: number } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setProblem(null);
    if (f.size > MAX_BILL) {
      setProblem("That file is over 10 MB.");
      return;
    }
    if (!/\.(pdf|png|jpe?g)$/i.test(f.name) && !/^(application\/pdf|image\/(png|jpeg))$/.test(f.type)) {
      setProblem("Upload a PDF, PNG or JPEG.");
      return;
    }
    setUploading(true);
    try {
      const p = await uploadBill(port.id, f);
      setFile({ name: f.name, size: f.size });
      onUploaded(p);
    } catch (err) {
      setProblem(
        err instanceof ApiError && err.code === "too_large" ? "That file is over 10 MB." : errorMessage(err),
      );
    } finally {
      setUploading(false);
    }
  };

  const has = port.bill_uploaded || file !== null;
  return (
    <Panel title="Bill" help="A bill from the last 30 days. PDF, PNG or JPEG, up to 10 MB.">
      <input
        ref={input}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
        className="sr-only"
        aria-label="Bill copy"
        tabIndex={-1}
        onChange={(e) => void pick(e)}
      />
      {has ? (
        <div className="flex items-center gap-3 rounded-lg border border-hairline px-3 py-2.5">
          <span className="flex size-8 items-center justify-center rounded-md bg-fill-1 text-ink-2">
            <FileText className="size-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-ui">{file?.name ?? "Bill copy"}</p>
            {file ? <p className="text-caption text-ink-3">{fileSize(file.size)}</p> : null}
          </div>
          {readOnly ? null : (
            <Button variant="ghost" size="sm" onClick={() => input.current?.click()} disabled={uploading}>
              {uploading ? <Spinner /> : null}
              Replace
            </Button>
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={readOnly || uploading}
          onClick={() => input.current?.click()}
          className="flex h-24 flex-col items-center justify-center gap-1 rounded-lg border border-hairline-strong border-dashed text-caption text-ink-3 hover:bg-fill-1"
        >
          {uploading ? <Spinner /> : <Upload className="size-4" aria-hidden />}
          Upload bill
        </button>
      )}
      {problem ? (
        <p role="alert" className="text-caption text-danger-ink">
          {problem}
        </p>
      ) : null}
    </Panel>
  );
}

function LandingPanel({
  form,
  readOnly,
  onChange,
}: {
  form: PortForm;
  readOnly: boolean;
  onChange: (e164: string, patch: Partial<PortForm["numbers"][number]>) => void;
}) {
  const brands = useQuery(brandsQuery);
  const users = useQuery(usersQuery);
  const addresses = useQuery({ ...addressesQuery, enabled: !readOnly });
  const settings = useQuery({ ...purchaseSettingsQuery, enabled: !readOnly });
  const [adding, setAdding] = useState<string | null>(null);
  const usable = useMemo(
    () => (addresses.data ?? []).filter((a) => a.registration !== "failed"),
    [addresses.data],
  );
  const brandOptions = [
    { value: "", label: "Company-wide" },
    ...(brands.data ?? []).map((b) => ({ value: b.id, label: b.name })),
  ];
  const ownerOptions = [
    { value: "", label: "Unassigned" },
    ...(users.data ?? [])
      .filter((u) => u.status === "active")
      .map((u) => ({ value: u.id, label: u.name || u.email })),
  ];
  return (
    <>
      <Surface asChild className="flex min-w-0 flex-col">
        <section aria-label="Where they land">
          <header className="flex min-h-12 items-center px-4 pt-2">
            <h2 className="font-medium text-title">Where they land</h2>
          </header>
          <Table>
            <THead>
              <Tr>
                <Th>Number</Th>
                <Th>Brand</Th>
                <Th>Owner</Th>
                <Th>Label</Th>
                <Th numeric>911 address</Th>
              </Tr>
            </THead>
            <TBody>
              {form.numbers.map((n) => {
                const phone = fmtPhone(n.e164);
                return (
                  <Tr key={n.e164}>
                    <Td className="font-mono tabular-nums">{phone}</Td>
                    <Td>
                      <Select
                        aria-label={`Brand for ${phone}`}
                        value={n.brand_id}
                        disabled={readOnly}
                        onValueChange={(v) => onChange(n.e164, { brand_id: v })}
                        options={brandOptions}
                      />
                    </Td>
                    <Td>
                      <Select
                        aria-label={`Owner for ${phone}`}
                        value={n.owner_user_id}
                        disabled={readOnly}
                        onValueChange={(v) => onChange(n.e164, { owner_user_id: v })}
                        options={ownerOptions}
                      />
                    </Td>
                    <Td>
                      <Input
                        aria-label={`Label for ${phone}`}
                        value={n.label}
                        onChange={(e) => onChange(n.e164, { label: e.target.value })}
                      />
                    </Td>
                    <Td>
                      {n.number_type === "toll_free" ? (
                        <span className="text-caption text-ink-3">Not needed</span>
                      ) : (
                        <Select
                          aria-label={`911 address for ${phone}`}
                          value={n.emergency_address_id}
                          disabled={readOnly}
                          placeholder="Pick an address"
                          onValueChange={(v) =>
                            v === ADD ? setAdding(n.e164) : onChange(n.e164, { emergency_address_id: v })
                          }
                          options={[
                            ...usable.map((a) => ({ value: a.id, label: a.label || addressLine(a) })),
                            { value: ADD, label: "Add address" },
                          ]}
                        />
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        </section>
      </Surface>
      {adding ? (
        <AddressDialog
          settings={settings.data}
          onClose={() => setAdding(null)}
          onSaved={(a) => {
            onChange(adding, { emergency_address_id: a.id });
            setAdding(null);
          }}
        />
      ) : null}
    </>
  );
}

// The status page --------------------------------------------------------------------------------

function Marker({ mark }: { mark: StepMark }) {
  if (mark === "done")
    return (
      <span className="flex size-6 items-center justify-center rounded-pill bg-success-soft text-success">
        <Check className="size-3.5" aria-hidden />
      </span>
    );
  if (mark === "problem")
    return (
      <span className="flex size-6 items-center justify-center rounded-pill bg-warning-soft text-warning">
        <AlertTriangle className="size-3.5" aria-hidden />
      </span>
    );
  if (mark === "current") return <span className="size-6 rounded-pill bg-accent-soft" />;
  return <span className="size-6 rounded-pill border-2 border-hairline-strong" />;
}

function StatusScreen({
  port,
  canEdit,
  onFix,
  onCancel,
}: {
  port: Port;
  canEdit: boolean;
  onFix: () => void;
  onCancel: () => void;
}) {
  const addresses = useQuery({ ...addressesQuery, enabled: canEdit });
  const steps = timeline(port);
  const ported = port.state === "ported";
  const live = port.state === "porting" || ported;
  const comment = port.carrier_comments[0];
  const addressName = (id: string | null) => {
    const a = id ? addresses.data?.find((x) => x.id === id) : undefined;
    return a ? a.label || addressLine(a) : null;
  };

  const statusCard = (
    <Surface asChild className="flex min-w-0 flex-col">
      <section aria-label="Status">
        <header className="flex min-h-12 items-center px-4 pt-2">
          <h2 className="font-medium text-title">Status</h2>
        </header>
        <ol className="flex flex-col px-4 pb-4">
          {steps.map((s, i) => (
            <li key={s.key} className="relative flex gap-3 pb-4 last:pb-0">
              {i < steps.length - 1 ? (
                <span aria-hidden className="absolute top-7 bottom-1 left-3 w-px bg-hairline-strong" />
              ) : null}
              <Marker mark={s.mark} />
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="text-ui">{s.title}</p>
                {s.key === "confirmed" && s.at ? (
                  <p className="text-caption text-ink-3">{focText(s.at)}</p>
                ) : s.at && s.mark !== "ahead" ? (
                  <p className="text-caption text-ink-3">{stepTime(s.at)}</p>
                ) : null}
                {s.key === "confirmed" && port.state === "foc_confirmed" ? (
                  <p className="text-caption text-ink-3">Keep {port.losing_carrier} service on until then.</p>
                ) : null}
                {s.key === "fix" && comment ? (
                  <div className="mt-2 flex flex-col items-start gap-2">
                    <blockquote className="border-hairline-strong border-l-2 pl-3 text-ui">
                      {comment.text}
                    </blockquote>
                    {canEdit ? (
                      <div className="flex items-center gap-1.5">
                        <Button variant="primary" size="sm" onClick={onFix}>
                          Fix
                        </Button>
                        <InfoButton kind="help" label="About fixing a port">
                          You sign again after a fix.
                        </InfoButton>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </section>
    </Surface>
  );

  const testNumber = port.numbers[0]?.e164;
  return (
    <>
      <Header
        port={port}
        meta={`${numbersCount(port.numbers.length)}. ${port.account_holder}`}
        actions={
          canEdit && canCancel(port.state) ? (
            <Button variant="ghost" onClick={onCancel}>
              Cancel port
            </Button>
          ) : null
        }
      />
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        {ported ? (
          <p className="rounded-lg bg-success-soft px-4 py-3 text-success text-ui lg:col-span-2">
            Calls are arriving on Kite.
          </p>
        ) : null}
        {statusCard}
        <div className="flex min-w-0 flex-col gap-4">
          {live && testNumber ? (
            <Surface className="flex items-start gap-3 px-4 py-3">
              <Phone className="mt-0.5 size-4 text-ink-2" aria-hidden />
              <div>
                <p className="text-ui">Call it now to test</p>
                <p className="text-caption text-ink-3">Call {fmtPhone(testNumber)} from any phone.</p>
              </div>
            </Surface>
          ) : null}
          <Surface asChild className="flex min-w-0 flex-col">
            <section aria-label="Numbers">
              <header className="flex min-h-12 items-center px-4 pt-2">
                <h2 className="font-medium text-title">Numbers</h2>
              </header>
              <ul>
                {port.numbers.map((n) => {
                  const st = numberState(port.state);
                  const where = [n.label, addressName(n.emergency_address_id)].filter(Boolean).join(". ");
                  return (
                    <li
                      key={n.e164}
                      className="flex min-h-14 items-center justify-between gap-3 border-hairline border-t px-4 py-2 first:border-t-0"
                    >
                      <div className="min-w-0">
                        <p className="font-mono text-ui tabular-nums">{fmtPhone(n.e164)}</p>
                        {where ? <p className="truncate text-caption text-ink-3">{where}</p> : null}
                      </div>
                      <Badge tone={st.tone}>
                        {st.busy ? <Spinner className="size-3" /> : null}
                        {st.label}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            </section>
          </Surface>
        </div>
      </div>
    </>
  );
}

function CancelDialog({ port, onClose }: { port: Port; onClose: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const cancel = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const p = await ports.cancel(port.id);
      qc.setQueryData(portKeys.port(p.id), p);
      void qc.invalidateQueries({ queryKey: portKeys.list });
      onClose();
    } catch (e) {
      toast(errorMessage(e), "danger");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Cancel this port?"
      description={`Your numbers stay with ${port.losing_carrier}.`}
      size="sm"
      tone="danger"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Keep
          </Button>
          <Button variant="danger" onClick={() => void cancel()} disabled={busy}>
            Cancel port
          </Button>
        </>
      }
    />
  );
}
